import { Ref, useId } from "react"

import { ReactComponent as IconBonuses } from "@common_assets/svg/bonuses.svg"
import { ReactComponent as IconFines } from "@common_assets/svg/finest.svg"
import { Box, Stack, Theme, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  IDashboardBehaviorRow,
  IDashboardBehaviorSelection,
} from "ts/interfaces/AdminDashboard"
import { DashboardBehaviorType } from "ts/types/AdminDashboard"

import { BEHAVIOR_CATEGORY_COLOR, isSameBehavior } from "./behaviorFormat"
import BehaviorTrend from "./BehaviorTrend"

const ROW_COLUMNS = "24px minmax(0, 1fr) 64px 64px"

const rowGridSx = {
  display: "grid",
  gridTemplateColumns: ROW_COLUMNS,
  columnGap: 1,
  alignItems: "center",
}

// Without `onSelect` the rows are plain list items; with it each row is a
// toggle that puts its behaviour on the chart.
const BehaviorList = ({
  type,
  rows,
  startRank = 1,
  selection = null,
  onSelect,
  selectedRef,
}: {
  type: DashboardBehaviorType
  rows: IDashboardBehaviorRow[]
  startRank?: number
  selection?: IDashboardBehaviorSelection | null
  onSelect?: (behavior: IDashboardBehaviorSelection) => void
  // Set to the selected row's toggle, if one is in the list
  selectedRef?: Ref<HTMLButtonElement>
}) => {
  const { t } = useTranslation()
  const headingId = useId()

  return (
    <Box minWidth={0} p={3}>
      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        {/* Decorative: the heading text names the list */}
        {type === "bonus" ? (
          <IconBonuses width={28} height={28} aria-hidden="true" />
        ) : (
          <IconFines width={28} height={28} aria-hidden="true" />
        )}
        <Typography
          id={headingId}
          variant="body1"
          component="h4"
          color="mockup.neutral0"
          fontWeight={700}
          fontSize="18px"
        >
          {t(
            type === "bonus"
              ? "adminDashboard.topBonuses"
              : "adminDashboard.topFines",
          )}
        </Typography>
      </Stack>
      <Box
        aria-hidden="true"
        sx={{
          display: "grid",
          gridTemplateColumns: ROW_COLUMNS,
          columnGap: 1,
          color: "mockup.neutral30",
          textTransform: "uppercase",
          mb: "14px",
          // Lines the columns up with the toggle rows' padding and bar
          ...(onSelect && { pl: "11px", pr: 1 }),
        }}
      >
        <Typography variant="body3" sx={{ gridColumn: 2 }}>
          {t("adminDashboard.behaviourColumn")}
        </Typography>
        <Typography variant="body3" textAlign="right">
          {t("adminDashboard.countColumn")}
        </Typography>
        <Typography variant="body3" textAlign="right">
          {t("adminDashboard.trendColumn")}
        </Typography>
      </Box>
      <Box
        component="ul"
        aria-labelledby={headingId}
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "grid",
          rowGap: onSelect ? 0 : "14px",
        }}
      >
        {rows.map((row, index) => {
          const behavior = { type, title: row.title }
          const isSelected = isSameBehavior(selection, behavior)
          const cells = (
            <>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "mockup.neutral95",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Typography variant="body3" color="mockup.neutral10">
                  {startRank + index}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                noWrap
                title={row.title}
                color="mockup.neutral10"
                fontWeight={isSelected ? 700 : 500}
              >
                {row.title}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                textAlign="right"
                color={BEHAVIOR_CATEGORY_COLOR[type]}
              >
                {row.item_count.toLocaleString("en-US")}
              </Typography>
              <Typography
                variant="body3"
                color="mockup.neutral30"
                fontWeight={500}
                textAlign="right"
              >
                <BehaviorTrend row={row} />
              </Typography>
            </>
          )

          if (!onSelect)
            return (
              <Box component="li" key={row.title} sx={rowGridSx}>
                {cells}
              </Box>
            )

          return (
            <li key={row.title}>
              <Box
                component="button"
                type="button"
                ref={isSelected ? selectedRef : undefined}
                aria-pressed={isSelected}
                onClick={() => onSelect(behavior)}
                sx={{
                  ...rowGridSx,
                  width: "100%",
                  m: 0,
                  py: "7px",
                  px: 1,
                  border: "none",
                  borderLeft: "3px solid transparent",
                  borderRadius: "8px",
                  bgcolor: "transparent",
                  font: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                  // An outline, not a fill: the counts drop under 4.5:1 on grey
                  "&:hover": {
                    boxShadow: (theme: Theme) =>
                      `inset 0 0 0 1px ${theme.palette.mockup.neutral90}`,
                  },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "mockup.primary60",
                    outlineOffset: "2px",
                  },
                  ...(isSelected && {
                    borderLeftColor: BEHAVIOR_CATEGORY_COLOR[type],
                    bgcolor: "mockup.neutral100",
                    boxShadow: (theme: Theme) =>
                      `inset 0 0 0 1px ${theme.palette.mockup.neutral90}`,
                  }),
                }}
              >
                {cells}
                <Box component="span" sx={visuallyHiddenSx}>
                  {t("adminDashboard.showOnChart")}
                </Box>
              </Box>
            </li>
          )
        })}
      </Box>
    </Box>
  )
}

export default BehaviorList
